"use client"

import { useState } from "react"
import { Card } from "@/components/ui/card"
import { Play, Loader2, Youtube, Pencil, Download, Share2, Trash2, MoreHorizontal } from "lucide-react"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import type { CaptionStyle } from "@/types/caption"
import { ClipCaptionPreview } from "./ClipCaptionPreview"
import { cardHookY } from "@/remotion/utils/placement"
import type { SceneMetadata } from "@/remotion/schemas"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"

interface PublishRecord {
  id: string
  platform: string
  video_url: string
  status: string
}

export interface ClipData {
  id: string
  title: string
  clip_index: number
  start_time: number
  end_time: number
  duration: number
  virality_score: number
  virality_reason?: string
  status: string
  preview_url?: string
  thumbnail_url?: string | null  // Custom saved thumbnail
  publish_history?: PublishRecord[]
  auto_hook?: {
    enabled: boolean
    textOverlay?: { hookText: string }
    hookType?: string
    strengthScore?: number
  } | null
  rendered_url?: string | null  // Presigned S3 URL for rendered clip
  editing_script?: { renderPref?: { enableCaption?: boolean }; sceneMetadata?: SceneMetadata } | null
  first_caption?: string | null  // First caption text for thumbnail display
  scene_count?: number  // Layout scenes in the editing script; more than one means the speakers change
  max_faces?: number
  layout_changes?: number  // Scene boundaries where the layout (who is on screen) changes
  caption_style?: Partial<CaptionStyle> | null
}

interface ClipCardProps {
  clip: ClipData
  clipIndex?: number
  onClick?: () => void
  onDownload?: () => void
  onPublish?: () => void
  onDelete?: () => Promise<void>
}

/** "3 speaker changes · up to 2 people"; every card shows its count (even "No speaker changes") so cards compare at a glance. */
export function clipScenesLabel(clip: Pick<ClipData, "layout_changes" | "max_faces">): string {
  const changes = clip.layout_changes ?? 0
  const people = clip.max_faces ?? 0
  const changeText = changes === 0 ? "No speaker changes" : `${changes} speaker change${changes === 1 ? "" : "s"}`
  return people > 1 ? `${changeText} · up to ${people} people` : changeText
}

function formatDuration(seconds: number): string {
  const mins = Math.floor(seconds / 60)
  const secs = Math.floor(seconds % 60)
  if (mins > 0) {
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }
  return `${secs}s`
}

/** Normalize score to 0-100 regardless of whether backend sends 0-1 or 0-100 */
function normalizeScore(score: number): number {
  return score <= 1 ? Math.round(score * 100) : Math.round(score)
}

function getScoreTextColor(score: number): string {
  const n = normalizeScore(score)
  if (n >= 90) return "text-emerald-500"
  if (n >= 75) return "text-lime-500"
  if (n >= 60) return "text-yellow-500"
  return "text-muted-foreground"
}

function getPublishedPlatforms(publishHistory?: PublishRecord[]): string[] {
  if (!publishHistory) return []
  return [...new Set(
    publishHistory
      .filter(p => p.status === "success")
      .map(p => p.platform)
  )]
}

function getPlatformIcon(platform: string) {
  switch (platform) {
    case "youtube":
      return <Youtube className="w-3 h-3" />
    default:
      return null
  }
}

/** The card hook box's height as a share of the card (two lines of 10px text). */
const CARD_HOOK_HEIGHT = 0.1

export function ClipCard({ clip, clipIndex, onClick, onDownload, onPublish, onDelete }: ClipCardProps) {
  const isRendering = clip.status === "rendering" || clip.status === "queued"
  // The hook sits where the video puts it for the thumbnail's frame: on a split's seam, clear of faces.
  const hookY = cardHookY(clip.editing_script?.sceneMetadata, clip.duration, CARD_HOOK_HEIGHT)
  const isRendered = clip.status === "rendered"
  const publishedPlatforms = getPublishedPlatforms(clip.publish_history)
  const scorePercent = normalizeScore(clip.virality_score)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const scenesLabel = clipScenesLabel(clip)
  const displayIndex = clipIndex !== undefined ? clipIndex : (clip.clip_index ?? 0)

  async function handleDeleteConfirm() {
    if (!onDelete) return
    setIsDeleting(true)
    try {
      await onDelete()
    } finally {
      setIsDeleting(false)
      setDeleteDialogOpen(false)
    }
  }

  return (
    <>
    <Card
      data-testid={`clip-${clip.id}`}
      className="overflow-hidden border-border/60 dark:border-white/[0.06] hover:border-[#CC3E21]/50 cursor-pointer transition-all group"
      onClick={onClick}
    >
      {/* Video Preview / Thumbnail */}
      {/* The clip's own 9:16, so the thumbnail is not cropped through the top speaker's head. */}
      <div className="relative aspect-[9/16] bg-muted" style={{ containerType: "inline-size" }}>
        {clip.thumbnail_url ? (
          <img
            src={clip.thumbnail_url}
            alt={clip.title}
            className="w-full h-full object-cover"
            loading="lazy"
          />
        ) : clip.preview_url ? (
          <video
            src={clip.preview_url}
            className="w-full h-full object-cover"
            muted
            playsInline
            preload="metadata"
            onLoadedMetadata={(e) => { e.currentTarget.currentTime = 0.1 }}
            onMouseEnter={(e) => {
              e.currentTarget.currentTime = 0
              e.currentTarget.play().catch(() => {})
            }}
            onMouseLeave={(e) => {
              e.currentTarget.pause()
              e.currentTarget.currentTime = 0.1
            }}
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center">
            {isRendering ? (
              <Loader2 className="w-8 h-8 text-[#CC3E21] animate-spin" />
            ) : (
              <Play className="w-12 h-12 text-white/50" />
            )}
          </div>
        )}

        {/* Duration Badge */}
        <div className="absolute bottom-1.5 right-1.5 bg-black/70 text-white text-[10px] px-1.5 py-0.5 rounded">
          {formatDuration(clip.duration)}
        </div>

        {/* Clip number badge */}
        <div className="absolute bottom-1.5 left-1.5 bg-black/70 text-white/90 text-[10px] font-medium px-1.5 py-0.5 rounded tabular-nums">
          #{displayIndex + 1}
        </div>

        {/* Play overlay — always visible on thumbnails, hover-only on videos */}
        {clip.thumbnail_url && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/20 group-hover:bg-black/40 transition-colors">
            <div className="w-10 h-10 rounded-full bg-black/60 flex items-center justify-center group-hover:scale-110 transition-transform">
              <Play className="w-5 h-5 text-white ml-0.5" fill="white" />
            </div>
          </div>
        )}
        {!clip.thumbnail_url && clip.preview_url && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity">
            <Play className="w-12 h-12 text-white" fill="white" />
          </div>
        )}


        {/* Text Hook Overlay */}
        {clip.auto_hook?.enabled && clip.auto_hook?.textOverlay?.hookText && (
          <div className="absolute left-2 right-2 z-10 -translate-y-1/2" style={{ top: `${hookY * 100}%` }}>
            <div className="bg-white/95 text-black text-[10px] font-bold px-2 py-1.5 rounded text-center line-clamp-2 shadow-sm">
              {clip.auto_hook.textOverlay.hookText}
            </div>
          </div>
        )}

        {/* The opening caption, in the clip's caption style and masked like the export */}
        {clip.editing_script?.renderPref?.enableCaption !== false && (
          <ClipCaptionPreview text={clip.first_caption} style={clip.caption_style} boxAspect={9 / 16} />
        )}

        {/* Published platforms badges */}
        {publishedPlatforms.length > 0 && (
          <div className="absolute top-2 right-2 flex gap-1">
            {publishedPlatforms.map(platform => (
              <div
                key={platform}
                className="bg-red-600 text-white p-1.5 rounded"
                title={`Published to ${platform}`}
              >
                {getPlatformIcon(platform)}
              </div>
            ))}
          </div>
        )}

      </div>

      {/* Card Content */}
      <div className="px-2 py-2 space-y-2">
        <div className="space-y-1">
          <span className={`block text-sm font-bold tabular-nums leading-none ${getScoreTextColor(clip.virality_score)}`}>
            {scorePercent}<span className="text-[10px] font-normal text-muted-foreground">%</span>
          </span>
          <h3 className="text-[11px] font-medium line-clamp-2 leading-tight">
            {clip.title}
          </h3>
          <span data-testid="clip-scenes" className="block text-[10px] text-muted-foreground leading-none">
            {scenesLabel}
          </span>
        </div>

        {/* Edit is the next step, so it gets the labeled primary button; the rest live in "More". */}
        <div className="flex items-center gap-1">
          <Button
            size="sm"
            className="h-8 flex-1 bg-[#CC3E21] hover:bg-[#b5351b] text-white text-xs"
            onClick={(e) => {
              e.stopPropagation()
              onClick?.()
            }}
          >
            <Pencil className="w-3.5 h-3.5" />
            Edit
          </Button>
          {isRendering && (
            <Loader2 className="w-3.5 h-3.5 animate-spin text-muted-foreground" aria-label="Rendering" />
          )}
          {((isRendered && (onDownload || onPublish)) || onDelete) && (
            // modal={false}: a modal menu leaves pointer-events locked when it hands off to the delete dialog.
            <DropdownMenu modal={false}>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 px-2 text-xs"
                  aria-label="More clip actions"
                  onClick={(e) => e.stopPropagation()}
                >
                  <MoreHorizontal className="w-4 h-4" />
                  More
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                {isRendered && onDownload && (
                  <DropdownMenuItem className="py-2" title="Download clip" onSelect={() => onDownload()}>
                    <Download />
                    Download
                  </DropdownMenuItem>
                )}
                {isRendered && onPublish && (
                  <DropdownMenuItem className="py-2" onSelect={() => onPublish()}>
                    <Share2 />
                    Publish
                  </DropdownMenuItem>
                )}
                {onDelete && (
                  <>
                    {isRendered && (onDownload || onPublish) && <DropdownMenuSeparator />}
                    <DropdownMenuItem
                      variant="destructive"
                      className="py-2"
                      onSelect={() => setDeleteDialogOpen(true)}
                    >
                      <Trash2 />
                      Delete…
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>
    </Card>

    {/* Delete confirmation dialog — rendered outside Card to avoid onClick bubbling issues */}
    <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete this clip?</AlertDialogTitle>
          <AlertDialogDescription>
            This action cannot be undone. The clip will be permanently removed.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleDeleteConfirm}
            disabled={isDeleting}
            className="bg-[#CC3E21] hover:bg-[#b83620] text-white"
          >
            {isDeleting ? (
              <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin inline" />Deleting...</>
            ) : (
              "Delete clip"
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </>
  )
}
